/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {type AnchoredOverlayAnchorProps, ProjectPicker, type ProjectPickerProject} from '@github-ui/item-picker-project'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {GlobalCommands} from '@github-ui/ui-commands'
import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {type Environment, fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'
import type {Subscription} from 'relay-runtime'
import type {addIssuesToProjectPickerBulkByQueryMutation$data} from '../mutations/__generated__/addIssuesToProjectPickerBulkByQueryMutation.graphql'
import type {addIssuesToProjectPickerBulkMutation$data} from '../mutations/__generated__/addIssuesToProjectPickerBulkMutation.graphql'
import {commitAddIssuesToProjectPickerBulkByQueryMutation} from '../mutations/add-issues-to-project-picker-bulk-by-query-mutation'
import {commitAddIssuesToProjectPickerBulkMutation} from '../mutations/add-issues-to-project-picker-bulk-mutation'
import type {ProjectPickerRelay$key, ProjectPickerRelay$data} from './__generated__/ProjectPickerRelay.graphql'
import type {
  ProjectPickerRelayQuery,
  ProjectPickerRelayQuery$data,
} from './__generated__/ProjectPickerRelayQuery.graphql'

export type ProjectPickerRelayProject = Omit<ProjectPickerRelay$data, ' $fragmentType'>

export function readProjectFromFragment(key: ProjectPickerRelay$key): ProjectPickerRelayProject {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, key)
}

const ProjectPickerRelayFragment = graphql`
  fragment ProjectPickerRelay on ProjectV2 @inline {
    id
    title
    closed
    number
    url
    viewerCanUpdate
    hasReachedItemsLimit
    __typename
  }
`

export const ProjectPickerGraphqlQuery = graphql`
  query ProjectPickerRelayQuery($owner: String!, $repo: String!, $query: String) {
    repository(owner: $owner, name: $repo) {
      projectsV2(first: 5, query: $query, orderBy: {field: RELEVANCE, direction: DESC}, useFullTermQuery: true) {
        nodes {
          ...ProjectPickerRelay
        }
      }
      recentProjects(first: 5) {
        edges {
          node {
            ...ProjectPickerRelay
          }
        }
      }
      owner {
        ... on Organization {
          projectsV2(first: 5, orderBy: {field: RELEVANCE, direction: DESC}, query: $query, useFullTermQuery: true) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
          recentProjects(first: 5) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
        }
        ... on User {
          projectsV2(first: 5, orderBy: {field: RELEVANCE, direction: DESC}, query: $query, useFullTermQuery: true) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
          recentProjects(first: 5) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
        }
      }
    }
  }
`

export async function fetchProjects(
  environment: Environment,
  options: {owner: string; repo: string; query: string},
): Promise<{
  repositoryProjects: ProjectPickerRelayProject[]
  recentRepositoryProjects: ProjectPickerRelayProject[]
  ownerProjects: ProjectPickerRelayProject[]
  recentOwnerProjects: ProjectPickerRelayProject[]
}> {
  const {owner, repo, query} = options

  const data = await fetchQuery<ProjectPickerRelayQuery>(
    environment,
    ProjectPickerGraphqlQuery,
    {
      owner,
      repo,
      query,
    },
    {fetchPolicy: 'store-or-network'},
  ).toPromise()

  const repositoryProjects = (data?.repository?.projectsV2?.nodes ?? [])
    .filter(node => !!node)
    .map(node => readProjectFromFragment(node))

  const recentRepositoryProjects = (data?.repository?.recentProjects?.edges ?? [])
    .map(edge => edge?.node)
    .filter(node => !!node)
    .map(node => readProjectFromFragment(node))

  const ownerProjects = (data?.repository?.owner?.projectsV2?.edges ?? [])
    .map(edge => edge?.node)
    .filter(node => !!node)
    .map(node => readProjectFromFragment(node))

  const recentOwnerProjects = (data?.repository?.owner?.recentProjects?.edges ?? [])
    .map(edge => edge?.node)
    .filter(node => !!node)
    .map(node => readProjectFromFragment(node))

  return {repositoryProjects, recentRepositoryProjects, ownerProjects, recentOwnerProjects}
}

export type ProjectPickerTestQueries = {ProjectPickerRelayQuery: ProjectPickerRelayQuery}

export type ProjectPickerRelayProps = {
  pickerId: string
  readonly?: boolean
  selectedProjects: ProjectPickerRelayProject[]
  firstSelectedProjectTitle?: string
  onSave: (projects: ProjectPickerRelayProject[]) => void
  anchorElement: (props: AnchoredOverlayAnchorProps) => React.ReactElement
  insidePortal?: boolean
  /**
   * Whether to render the project picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  owner: string
  repo: string
  triggerOpen?: boolean
  title?: string
  getSelectedProjects?: (projects: ProjectPickerRelayProject[]) => ProjectPickerRelayProject[]
}

// Bulk picker (legacy surface)
export type BulkProjectPickerProps = Omit<ProjectPickerRelayProps, 'onSave'> &
  Omit<SharedBulkActionsItemPickerProps, 'issuesToActOn'> & {
    issueIds: string[]
    onCompleted?: (jobId?: string) => void
    onError?: (error: Error) => void
    useQueryForAction?: boolean
    repositoryId?: string
    query?: string
  }

export const BulkProjectPicker: React.FC<BulkProjectPickerProps> = ({
  pickerId,
  useQueryForAction,
  repositoryId,
  query,
  issueIds,
  readonly,
  selectedProjects,
  firstSelectedProjectTitle,
  insidePortal,
  anchorElement,
  onCompleted,
  onError,
  nested = false,
  owner,
  repo,
  triggerOpen = false,
}) => {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (newSelected: ProjectPickerRelayProject[]) => {
      const projectsToAdd = newSelected.filter(p => !selectedProjects.some(sp => sp.id === p.id))
      const projectsToRemove = selectedProjects.filter(p => !newSelected.some(np => np.id === p.id))
      if (!projectsToAdd.length && !projectsToRemove.length) return
      if (useQueryForAction && repositoryId && query) {
        commitAddIssuesToProjectPickerBulkByQueryMutation({
          environment,
          input: {
            repositoryId,
            query,
            addToProjectV2Ids: projectsToAdd.map(p => p.id),
            removeFromProjectV2Ids: projectsToRemove.map(p => p.id),
          },
          onCompleted: ({updateIssuesBulkByQuery}: addIssuesToProjectPickerBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitAddIssuesToProjectPickerBulkMutation({
          environment,
          input: {
            ids: [...issueIds],
            addToProjectV2Ids: projectsToAdd.map(p => p.id),
            removeFromProjectV2Ids: projectsToRemove.map(p => p.id),
          },
          onCompleted: ({updateIssuesBulk}: addIssuesToProjectPickerBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
          onError: (error: Error) => onError?.(error),
        })
      }
    },
    [environment, issueIds, onCompleted, onError, query, repositoryId, selectedProjects, useQueryForAction],
  )

  return (
    <ProjectPickerRelay
      pickerId={pickerId} // Should be issue specific, if loaded for a specific issue :)
      readonly={readonly}
      selectedProjects={[...selectedProjects]} // Clone so we keep the initial state
      firstSelectedProjectTitle={firstSelectedProjectTitle}
      insidePortal={insidePortal}
      onSave={onSelectionChange}
      anchorElement={anchorElement}
      nested={nested}
      owner={owner}
      repo={repo}
      triggerOpen={triggerOpen}
    />
  )
}

// Public component – legacy props
export function ProjectPickerRelay(props: ProjectPickerRelayProps) {
  const triggerOpen = !!props.triggerOpen
  const [wasTriggered, setWasTriggered] = useState(triggerOpen)
  const handleGlobalCommand = useCallback(() => {
    if (!wasTriggered) setWasTriggered(true)
  }, [wasTriggered])

  if (!wasTriggered) {
    return (
      <>
        <GlobalCommands commands={{'item-pickers:open-projects': handleGlobalCommand}} />
        {props.anchorElement({
          onClick: () => setWasTriggered(true),
          onKeyPress: (event: React.KeyboardEvent<HTMLLIElement>) => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setWasTriggered(true)
            }
          },
        })}
      </>
    )
  }

  return (
    <ItemPickerFetcher
      pickerId={props.pickerId}
      triggerOpen
      renderAnchor={props.anchorElement}
      initialSelectedItems={[...props.selectedProjects]}
      onSaveProjects={items => props.onSave(items)}
      getSelectedProjects={props.getSelectedProjects}
      readonly={props.readonly}
      firstSelectedProjectTitle={props.firstSelectedProjectTitle}
      insidePortal={props.insidePortal}
      nested={props.nested}
      owner={props.owner}
      repo={props.repo}
      title={props.title}
    />
  )
}

function ItemPickerFetcher({
  pickerId, // unused but helpful for debugging & future parity
  repo,
  owner,
  initialSelectedItems,
  onSaveProjects,
  getSelectedProjects,
  renderAnchor,
  triggerOpen,
  readonly,
  firstSelectedProjectTitle,
  insidePortal,
  nested,
  title,
}: {
  pickerId: string
  repo: string
  owner: string
  initialSelectedItems: ProjectPickerRelayProject[]
  onSaveProjects: (items: ProjectPickerRelayProject[]) => void
  getSelectedProjects?: (p: ProjectPickerRelayProject[]) => ProjectPickerRelayProject[]
  renderAnchor: (p: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  triggerOpen?: boolean
  readonly?: boolean
  firstSelectedProjectTitle?: string
  insidePortal?: boolean
  nested?: boolean
  title?: string
}) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const inFlightSubscriptionRef = useRef<Subscription | null>(null)
  const [data, setData] = useState<ProjectPickerRelayQuery$data | null>(null)
  const {addToast} = useToastContext()

  useEffect(() => {
    if (IS_SERVER) return
    clientSideRelayFetchQueryRetained<ProjectPickerRelayQuery>({
      environment,
      query: ProjectPickerGraphqlQuery,
      variables: {owner, repo},
    }).subscribe({
      next: internalData => {
        setData(internalData)
        setIsLoading(false)
        setIsError(false)
      },
      error: () => setIsError(true),
    })
  }, [environment, fetchKey, owner, repo])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit projects right now',
    anchorElement: renderAnchor,
    open: true,
  })

  const fetchSearchData = useCallback(
    (searchQuery: string) => {
      setIsLoading(true)
      const subscription = fetchQuery<ProjectPickerRelayQuery>(environment, ProjectPickerGraphqlQuery, {
        repo,
        owner,
        query: searchQuery,
      }).subscribe({
        next: (queryData: ProjectPickerRelayQuery$data) => {
          if (queryData) setData(queryData)
          setIsLoading(false)
        },
        error: () => {
          setIsLoading(false)
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.couldNotSearchProjects})
        },
      })
      inFlightSubscriptionRef.current = subscription
    },
    [owner, environment, repo, addToast],
  )

  const filterResetFn = useCallback(() => inFlightSubscriptionRef.current?.unsubscribe(), [])

  const recentProjects = useMemo(
    () =>
      (data?.repository?.owner?.recentProjects?.edges || []).flatMap(a =>
        a?.node
          ? [
              // eslint-disable-next-line no-restricted-syntax
              readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, a.node),
            ].filter(i => i.closed === false)
          : [],
      ),
    [data],
  )
  const repositoryProjects = useMemo(
    () =>
      (data?.repository?.projectsV2?.nodes || []).flatMap(a =>
        a
          ? [
              // eslint-disable-next-line no-restricted-syntax
              readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, a),
            ].filter(i => i.closed === false)
          : [],
      ),
    [data],
  )
  const organizationProjects = useMemo(
    () =>
      (data?.repository?.owner?.projectsV2?.edges || []).flatMap(a =>
        a?.node
          ? [
              // eslint-disable-next-line no-restricted-syntax
              readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, a.node),
            ].filter(i => i.closed === false)
          : [],
      ),
    [data],
  )
  const allProjects = useMemo(() => {
    const all = [...repositoryProjects, ...organizationProjects, ...recentProjects]
    const byId = new Map<string, ProjectPickerRelayProject>()
    for (const p of all) if (!byId.has(p.id)) byId.set(p.id, p)
    return Array.from(byId.values())
  }, [organizationProjects, recentProjects, repositoryProjects])

  if (isError) return createFallbackComponent(() => setFetchKey(fetchKey + 1))

  return (
    <ProjectPicker
      pickerId={pickerId}
      owner={owner}
      repo={repo}
      items={allProjects}
      loading={isLoading}
      filterItems={fetchSearchData}
      filterResetFn={filterResetFn}
      recentProjects={recentProjects}
      repositoryProjects={repositoryProjects}
      organizationProjects={organizationProjects}
      initialSelectedItems={initialSelectedItems}
      onSelectionChange={wrapProjectsFunction(onSaveProjects)}
      getSelectedProjects={getSelectedProjects && wrapProjectsFunction(getSelectedProjects)}
      renderAnchor={renderAnchor}
      triggerOpen={triggerOpen}
      readonly={readonly}
      firstSelectedProjectTitle={firstSelectedProjectTitle}
      insidePortal={insidePortal}
      nested={nested}
      title={title}
    />
  )
}

/**
 * Bridge between Relay Project and base Project types
 */
function mapProjectToRelayProject(project: ProjectPickerProject): ProjectPickerRelayProject {
  return '__typename' in project
    ? (project as ProjectPickerRelayProject)
    : ({...project, __typename: 'ProjectV2'} as ProjectPickerRelayProject)
}

/**
 * a wrapper HOC that takes a function and wraps it in a function that calls that func with
 * its projects prop mapped from ProjectPickerProject[] to ProjectPickerRelayProject[]
 */
function wrapProjectsFunction<R extends void | ProjectPickerRelayProject[]>(
  func: (projects: ProjectPickerRelayProject[]) => R,
): (projects: ProjectPickerProject[]) => R {
  return (projects: ProjectPickerProject[]) => {
    const mappedProjects = projects.map(mapProjectToRelayProject)
    return func(mappedProjects)
  }
}

try{ BulkProjectPicker.displayName ||= 'BulkProjectPicker' } catch {}
try{ ProjectPickerRelay.displayName ||= 'ProjectPickerRelay' } catch {}
try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}